import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  ShieldCheck, Clock, Check, X, Info, Loader2,
  User, Calendar, ArrowRight, Ban, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, XCircle, CheckCircle2, AlertTriangle
} from 'lucide-react';

import api from '../../services/api';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

// ============================================================================
// SECTION — Demandes en approbation v3
// ============================================================================

const STATUT_APPRO = {
  tous: { label: 'Toutes', color: '#448696', bg: '#f0f9fb', icon: ShieldCheck },
  en_attente: { label: 'En attente', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  en_cours: { label: 'En cours', color: '#3b82f6', bg: '#eff6ff', icon: ArrowRight },
  approuvee: { label: 'Approuvée', color: '#10b981', bg: '#d4f4dd', icon: CheckCircle2 },
  rejetee: { label: 'Rejetée', color: '#ef4444', bg: '#fee2e2', icon: XCircle },
  annulee: { label: 'Annulée', color: '#9ca3af', bg: '#f3f4f6', icon: Ban },
};

const PRIORITE_COLORS = {
  basse: '#9ca3af',
  normale: '#3b82f6',
  haute: '#f59e0b',
  urgente: '#ef4444',
};

const PAR_PAGE = 8;

// Header config basé sur l'état
const getHeaderStyle = (nbEnAttente, totalDemandes) => {
  if (totalDemandes === 0) return null; // ne pas afficher
  if (nbEnAttente > 0) return {
    bg: '#fffbeb',
    border: '#fcd34d',
    iconBg: '#fef3c7',
    iconColor: '#d97706',
    accentColor: '#92400e',
  };
  return {
    bg: '#f0fdf4',
    border: '#bbf7d0',
    iconBg: '#dcfce7',
    iconColor: '#16a34a',
    accentColor: '#166534',
  };
};

/**
 * @param {string} typeDocument - 'depense' | 'transfert' | 'commande'
 * @param {string} titre - Titre affiché
 * @param {function} onRefresh - Callback pour recharger le module parent
 * @param {ref} ref - Permet au parent d'appeler refresh() depuis l'extérieur
 */
const DemandesApprobationSection = forwardRef(({ typeDocument, titre, onRefresh }, ref) => {
  const [demandes, setDemandes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [filterStatut, setFilterStatut] = useState('tous');
  const [page, setPage] = useState(1);
  const [confirmAnnuler, setConfirmAnnuler] = useState(null);

  const { formatMontant } = useParamsCompta();

  // Expose refresh au parent via ref
  useImperativeHandle(ref, () => ({
    refresh: () => loadDemandes()
  }));

  useEffect(() => { loadDemandes(); }, [typeDocument]);

  const loadDemandes = async () => {
    setIsLoading(true);
    try {
      const r = await api.approbations.lister(`type_document=${typeDocument}`);
      if (r.success) setDemandes(r.data || []);
    } catch (e) {
      console.error('Erreur chargement demandes approbation:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnnuler = async (id) => {
    try {
      const r = await api.approbations.annuler(id);
      if (r.success) {
        setConfirmAnnuler(null);
        await loadDemandes();
        if (onRefresh) onRefresh();
      }
    } catch (e) { console.error('Erreur annulation:', e); }
  };

  useEffect(() => { setPage(1); }, [filterStatut]);

  // Compteurs
  const compteurs = demandes.reduce((acc, d) => {
    acc[d.statut] = (acc[d.statut] || 0) + 1;
    return acc;
  }, {});
  const nbEnAttente = (compteurs.en_attente || 0) + (compteurs.en_cours || 0);
  const nbApprouvees = compteurs.approuvee || 0;
  const nbRejetees = compteurs.rejetee || 0;

  // Filtrage
  const filtered = filterStatut === 'tous'
    ? demandes
    : filterStatut === 'en_attente'
      ? demandes.filter(d => d.statut === 'en_attente' || d.statut === 'en_cours')
      : demandes.filter(d => d.statut === filterStatut);

  // Pagination
  const totalPages = Math.ceil(filtered.length / PAR_PAGE);
  const paginated = filtered.slice((page - 1) * PAR_PAGE, page * PAR_PAGE);

  const montantEnAttente = demandes
    .filter(d => d.statut === 'en_attente' || d.statut === 'en_cours')
    .reduce((s, d) => s + parseFloat(d.montant || 0), 0);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  // Ne rien afficher si aucune demande
  if (!isLoading && demandes.length === 0) return null;

  const headerStyle = getHeaderStyle(nbEnAttente, demandes.length);
  if (!headerStyle && !isLoading) return null;

  return (
    <div className="mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* ── Header cliquable ── */}
      <div
        className="flex items-center gap-3 px-5 py-4 rounded-2xl cursor-pointer transition-all"
        style={{
          backgroundColor: headerStyle?.bg || '#f8fafc',
          border: `1.5px solid ${headerStyle?.border || '#e2e8f0'}`,
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: headerStyle?.iconBg }}>
          <ShieldCheck size={20} style={{ color: headerStyle?.iconColor }} />
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2.5">
            <span className="text-[15px] font-bold" style={{ color: '#1a1a2e' }}>
              {titre || 'Demandes en approbation'}
            </span>

            {nbEnAttente > 0 && (
              <span className="text-xs px-2.5 py-1 rounded-lg font-bold text-white flex items-center gap-1"
                style={{ backgroundColor: '#f59e0b' }}>
                <Clock size={11} />
                {nbEnAttente} en attente
              </span>
            )}

            {nbEnAttente === 0 && demandes.length > 0 && (
              <span className="text-xs px-2.5 py-1 rounded-lg font-bold flex items-center gap-1"
                style={{ backgroundColor: '#dcfce7', color: '#166534' }}>
                <CheckCircle2 size={11} />
                Tout est traité
              </span>
            )}

            {/* Tooltip */}
            <div className="relative group" onClick={(e) => e.stopPropagation()}>
              <Info size={14} className="text-gray-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-4 bg-gray-900 text-white text-xs rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <p className="font-bold mb-2 text-sm">Comment fonctionne l'approbation ?</p>
                <div className="space-y-2 text-gray-300 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <Clock size={12} className="text-amber-400 mt-0.5 shrink-0" />
                    <span><strong className="text-amber-400">En attente</strong> — L'opération dépasse le seuil autorisé et attend la validation d'un responsable.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={12} className="text-green-400 mt-0.5 shrink-0" />
                    <span><strong className="text-green-400">Approuvée</strong> — Validée par un responsable. L'opération est exécutée et visible dans la liste principale.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <XCircle size={12} className="text-red-400 mt-0.5 shrink-0" />
                    <span><strong className="text-red-400">Rejetée</strong> — Refusée par un responsable. L'opération n'est pas exécutée.</span>
                  </div>
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-gray-900" />
              </div>
            </div>
          </div>

          {/* Sous-titre contextuel */}
          <p className="text-xs font-medium mt-1" style={{ color: headerStyle?.accentColor || '#6b7280' }}>
            {nbEnAttente > 0
              ? `${nbEnAttente} opération${nbEnAttente > 1 ? 's' : ''} en attente de validation · ${formatMontant(montantEnAttente)}`
              : `${demandes.length} demande${demandes.length > 1 ? 's' : ''} traitée${demandes.length > 1 ? 's' : ''} — historique conservé`
            }
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Mini résumé chiffré */}
          {!isCollapsed && demandes.length > 3 && (
            <div className="hidden md:flex items-center gap-2 text-xs font-semibold">
              {nbApprouvees > 0 && (
                <span className="flex items-center gap-1 px-2 py-1 rounded-md" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>
                  <Check size={10} />{nbApprouvees}
                </span>
              )}
              {nbRejetees > 0 && (
                <span className="flex items-center gap-1 px-2 py-1 rounded-md" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
                  <X size={10} />{nbRejetees}
                </span>
              )}
            </div>
          )}
          {isCollapsed
            ? <ChevronDown size={20} className="text-gray-400" />
            : <ChevronUp size={20} className="text-gray-400" />}
        </div>
      </div>

      {/* ── Contenu déplié ── */}
      {!isCollapsed && (
        <div className="mt-2 rounded-2xl border overflow-hidden"
          style={{ borderColor: '#e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>

          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-300" />
            </div>
          ) : (
            <>
              {/* ── Filtres ── */}
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2 flex-wrap">
                {['tous', 'en_attente', 'approuvee', 'rejetee', 'annulee'].map((key) => {
                  const cfg = STATUT_APPRO[key];
                  const isActive = filterStatut === key;
                  const count = key === 'tous'
                    ? demandes.length
                    : key === 'en_attente'
                      ? nbEnAttente
                      : (compteurs[key] || 0);

                  if (key !== 'tous' && count === 0) return null;

                  return (
                    <button
                      key={key}
                      onClick={() => setFilterStatut(key)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                      style={isActive
                        ? { backgroundColor: cfg.bg, color: cfg.color, border: `1.5px solid ${cfg.color}40` }
                        : { backgroundColor: 'white', color: '#6b7280', border: '1.5px solid #e5e7eb' }
                      }
                    >
                      {cfg.label}
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                        style={isActive
                          ? { backgroundColor: cfg.color + '20', color: cfg.color }
                          : { backgroundColor: '#f3f4f6', color: '#9ca3af' }
                        }
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* ── Liste ── */}
              {filtered.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm font-medium">Aucune demande avec ce filtre</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100" style={{ maxHeight: '440px', overflowY: 'auto' }}>
                  {paginated.map((d) => {
                    const st = STATUT_APPRO[d.statut] || STATUT_APPRO.en_attente;
                    const StIcon = st.icon;
                    const isActive = d.statut === 'en_attente' || d.statut === 'en_cours';
                    const prioriteColor = PRIORITE_COLORS[d.priorite] || PRIORITE_COLORS.normale;

                    return (
                      <div
                        key={d.id}
                        className="px-5 py-4 transition-colors hover:bg-gray-50"
                        style={{ backgroundColor: isActive ? '#fffdf7' : '#ffffff' }}
                      >
                        <div className="flex items-center gap-3">
                          {/* Icône */}
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                            style={{ backgroundColor: st.bg }}>
                            <StIcon size={18} style={{ color: st.color }} />
                          </div>

                          {/* Contenu */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-bold text-gray-900 truncate">
                                {d.description || 'Demande #' + d.id}
                              </span>
                              <span className="text-[10px] px-2 py-0.5 rounded-md font-bold shrink-0"
                                style={{ backgroundColor: st.bg, color: st.color }}>
                                {st.label}
                              </span>
                              {d.priorite && d.priorite !== 'normale' && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded-md font-bold shrink-0"
                                  style={{ backgroundColor: prioriteColor + '15', color: prioriteColor }}
                                >
                                  {d.priorite}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-400 font-medium">
                              {d.demandeur_nom && (
                                <span className="flex items-center gap-1"><User size={10} />{d.demandeur_nom}</span>
                              )}
                              <span className="flex items-center gap-1"><Calendar size={10} />{formatDate(d.date_demande)}</span>
                              {d.niveau_requis > 1 && (
                                <span className="flex items-center gap-1">
                                  <ShieldCheck size={10} />Niv. {d.niveau_actuel}/{d.niveau_requis}
                                </span>
                              )}
                            </div>

                            {/* Messages contextuels */}
                            {d.statut === 'approuvee' && (
                              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium" style={{ color: '#166534' }}>
                                <CheckCircle2 size={12} />
                                <span>Validée — l'opération a été exécutée avec succès</span>
                              </div>
                            )}
                            {d.statut === 'rejetee' && (
                              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium" style={{ color: '#991b1b' }}>
                                <XCircle size={12} />
                                <span>Refusée par un responsable{d.date_resolution ? ` le ${formatDate(d.date_resolution)}` : ''}</span>
                              </div>
                            )}
                            {d.statut === 'annulee' && (
                              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-400">
                                <Ban size={12} />
                                <span>Annulée par le demandeur</span>
                              </div>
                            )}
                            {isActive && (
                              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium" style={{ color: '#92400e' }}>
                                <AlertTriangle size={12} />
                                <span>En attente de validation par un responsable</span>
                              </div>
                            )}
                          </div>

                          {/* Montant */}
                          <div className="text-right shrink-0">
                            <div className="text-sm font-bold" style={{ color: isActive ? '#92400e' : '#374151' }}>
                              {formatMontant(d.montant)}
                            </div>
                          </div>

                          {/* Bouton annuler */}
                          {isActive && (
                            <>
                              {confirmAnnuler === d.id ? (
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleAnnuler(d.id); }}
                                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all"
                                    style={{ backgroundColor: '#ef4444' }}
                                  >
                                    Confirmer
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setConfirmAnnuler(null); }}
                                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-600 bg-gray-100 transition-all hover:bg-gray-200"
                                  >
                                    Non
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setConfirmAnnuler(d.id); }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:bg-red-50 shrink-0"
                                  style={{ color: '#dc2626', border: '1.5px solid #fecaca' }}
                                >
                                  <Ban size={12} />
                                  Annuler
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Pagination ── */}
              {totalPages > 1 && (
                <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                  <span className="text-xs text-gray-400 font-medium">
                    {(page - 1) * PAR_PAGE + 1}–{Math.min(page * PAR_PAGE, filtered.length)} sur {filtered.length}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="w-8 h-8 rounded-lg flex items-center justify-center transition-all disabled:opacity-30"
                      style={{ backgroundColor: page > 1 ? '#f0f9fb' : 'transparent' }}
                    >
                      <ChevronLeft size={16} style={{ color: '#448696' }} />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                      .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) =>
                        p === '...' ? (
                          <span key={`dots-${i}`} className="text-xs text-gray-400 px-1">…</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all"
                            style={page === p
                              ? { backgroundColor: '#448696', color: 'white' }
                              : { color: '#6b7280' }
                            }
                          >
                            {p}
                          </button>
                        )
                      )}

                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="w-8 h-8 rounded-lg flex items-center justify-center transition-all disabled:opacity-30"
                      style={{ backgroundColor: page < totalPages ? '#f0f9fb' : 'transparent' }}
                    >
                      <ChevronRight size={16} style={{ color: '#448696' }} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
});

DemandesApprobationSection.displayName = 'DemandesApprobationSection';

export default DemandesApprobationSection;